import React, { useMemo } from 'react';

import { createContext } from '@lyearn/core/utils/context';
import { useAppConfig } from '@/hooks/useAppConfig';

import usePersonKnowledgeDiscovery, {
  PersonKnowledgeDiscoveryStatus,
} from './hooks/usePersonKnowledgeDiscovery';
import type { EnrichmentEntry } from './enrichmentData';

type PersonKnowledgeDiscoveryContextType = {
  cancelPolling: () => void;
  entries: EnrichmentEntry[];
  hasFetchedEntries: boolean;
  isEnabled: boolean;
  startPolling: (personId?: string) => void;
  status: PersonKnowledgeDiscoveryStatus;
  trigger: (
    personId?: string,
    email?: string,
    displayName?: string,
    companyName?: string,
  ) => Promise<void>;
};

const [PersonKnowledgeDiscoveryContextProvider, usePersonKnowledgeDiscoveryContext] =
  createContext<PersonKnowledgeDiscoveryContextType>('PersonKnowledgeDiscoveryContext');

export const PersonKnowledgeDiscoveryProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const {
    workspaceId,
    site: { isUserOnboardingAiEnabled },
  } = useAppConfig();
  const discovery = usePersonKnowledgeDiscovery(workspaceId);

  const value = useMemo(
    () => ({
      cancelPolling: discovery.cancelPolling,
      entries: discovery.entries,
      hasFetchedEntries: discovery.hasFetchedEntries,
      isEnabled: Boolean(isUserOnboardingAiEnabled),
      startPolling: discovery.startPolling,
      status: discovery.status,
      trigger: discovery.trigger,
    }),
    [
      discovery.cancelPolling,
      discovery.entries,
      discovery.hasFetchedEntries,
      discovery.startPolling,
      discovery.status,
      discovery.trigger,
      isUserOnboardingAiEnabled,
    ],
  );

  return (
    <PersonKnowledgeDiscoveryContextProvider {...value}>
      {children}
    </PersonKnowledgeDiscoveryContextProvider>
  );
};

export { usePersonKnowledgeDiscoveryContext };
